/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import {
  Braces,
  Building2,
  CalendarDays,
  ChevronRight,
  CreditCard,
  Menu,
  MessageSquare,
  PanelLeftClose,
  Search,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import { useCostingSettings } from '@/hooks/costing/useCostingSettings';
import { useFeatureFlags } from '@/stores/tenant.store';
import { useFullbleed } from '@/contexts/fullbleed';
import {
  SETTINGS_NAV_COLLAPSED_KEY,
  SETTINGS_NAV_TOGGLE_EVENT,
  SettingsWorkspaceLayoutProvider,
} from '@/contexts/settings-workspace-layout';
import { isNavItemVisible, SETTINGS_SECTION, type NavItem } from '@/components/layout/nav-config';
import { useAuthFieldMasks, useAuthPermissions, useAuthTechnicianId } from '@/stores/auth.store';
import { cn } from '@/lib/utils';

const GROUP_ORDER = [
  'Company',
  'Operations',
  'Sales & billing',
  'Customers',
  'Communications',
  'AI',
  'Developer & data',
] as const;

type SettingsGroupName = (typeof GROUP_ORDER)[number];

interface SettingsPresentation {
  group: SettingsGroupName;
  label?: string;
  description: string;
}

const SETTINGS_PRESENTATION: Record<string, SettingsPresentation> = {
  '/dashboard/settings/company': {
    group: 'Company',
    description: 'Legal name, address, licenses',
  },
  '/dashboard/settings/users': {
    group: 'Company',
    description: 'Seats, invites, deactivation',
  },
  '/dashboard/settings/permissions': {
    group: 'Company',
    description: 'Permission sets',
  },
  '/dashboard/settings/groups': {
    group: 'Company',
    description: 'Crews for dispatch and reporting',
  },
  '/dashboard/settings/skills': {
    group: 'Company',
    description: 'Certifications techs hold',
  },
  '/dashboard/settings/payroll-profiles': {
    group: 'Company',
    label: 'Payroll Profiles',
    description: 'Rates, overtime, commission',
  },
  '/dashboard/settings/office-settings': {
    group: 'Operations',
    description: 'Hours, arrival windows, capacity',
  },
  '/dashboard/settings/booking-windows': {
    group: 'Operations',
    description: 'Customer arrival windows by business unit',
  },
  '/dashboard/settings/oncall': {
    group: 'Operations',
    description: 'After-hours rotation',
  },
  '/dashboard/settings/online-booking': {
    group: 'Operations',
    description: 'Customer self-serve slots',
  },
  '/dashboard/settings/jobtypes': {
    group: 'Operations',
    description: 'The work you sell',
  },
  '/dashboard/settings/job-pipelines': {
    group: 'Operations',
    description: 'Stages from booked to paid',
  },
  '/dashboard/settings/contracts': {
    group: 'Operations',
    description: 'Work that repeats on a set cadence',
  },
  '/dashboard/settings/contracts/defaults': {
    group: 'Operations',
    description: 'Work that repeats on a set cadence',
  },
  '/dashboard/settings/service-areas': {
    group: 'Operations',
    description: 'Where you go, what travel costs',
  },
  '/dashboard/settings/trades': {
    group: 'Operations',
    description: 'Separate books per trade',
  },
  '/dashboard/pricebook': {
    group: 'Sales & billing',
    description: 'Tasks, parts, materials',
  },
  '/dashboard/settings/estimates': {
    group: 'Sales & billing',
    label: 'Estimates',
    description: 'Options, expiry, approvals',
  },
  '/dashboard/settings/client-portal': {
    group: 'Customers',
    label: 'Client portal',
    description: 'Let customers view invoices, estimates & visits',
  },
  '/dashboard/settings/templates': {
    group: 'Sales & billing',
    description: 'Estimate and proposal templates',
  },
  '/dashboard/settings/billing': {
    group: 'Sales & billing',
    label: 'Invoices',
    description: 'Numbering, terms, dunning',
  },
  '/dashboard/taxes': {
    group: 'Sales & billing',
    description: 'Rates by jurisdiction',
  },
  '/dashboard/settings/suppliers': {
    group: 'Sales & billing',
    label: 'Suppliers',
    description: 'Who you buy materials from',
  },
  '/dashboard/settings/purchasing': {
    group: 'Sales & billing',
    label: 'Purchasing',
    description: 'PO numbering, supplier terms, invoice matching',
  },
  '/dashboard/settings/terms-and-conditions': {
    group: 'Sales & billing',
    label: 'Terms & Conditions',
    description: 'Fine print on documents',
  },
  '/dashboard/settings/scope-of-work': {
    group: 'Sales & billing',
    label: 'Scope of Work',
    description: 'Reusable work descriptions',
  },
  '/dashboard/membership-plans': {
    group: 'Sales & billing',
    label: 'Membership Plans',
    description: 'Service agreements',
  },
  '/dashboard/settings/contact-statuses': {
    group: 'Customers',
    description: 'Where a contact stands',
  },
  '/dashboard/settings/tags': {
    group: 'Customers',
    description: 'Free-form labels',
  },
  '/dashboard/settings/leads': {
    group: 'Customers',
    description: 'Sources, routing, and SLA',
  },
  '/dashboard/settings/appointment-forms': {
    group: 'Customers',
    label: 'Forms',
    description: 'Checklists techs fill on site',
  },
  '/dashboard/settings/checklist': {
    group: 'Customers',
    description: 'Required steps for field work',
  },
  '/dashboard/settings/surveys': {
    group: 'Customers',
    description: 'Questions asked during booking',
  },
  '/dashboard/settings/reviews': {
    group: 'Customers',
    label: 'Reviews',
    description: 'When to ask, and where the ask points',
  },
  '/dashboard/settings/channels': {
    group: 'Communications',
    label: 'Inbox Channels',
    description: 'What lands in the shared inbox',
  },
  '/dashboard/settings/phone': {
    group: 'Communications',
    label: 'Phone Numbers',
    description: 'Numbers you own',
  },
  '/dashboard/settings/call-settings': {
    group: 'Communications',
    description: 'Ring order and voicemail',
  },
  '/dashboard/settings/sms': {
    group: 'Communications',
    description: 'Texting from office and field',
  },
  '/dashboard/settings/email-accounts': {
    group: 'Communications',
    label: 'Email Accounts',
    description: 'Mailboxes you send from',
  },
  '/dashboard/settings/reminders': {
    group: 'Communications',
    label: 'Customer Reminders',
    description: 'Nudges before and after a visit',
  },
  '/dashboard/settings/notifications': {
    group: 'Communications',
    label: 'Team Notifications',
    description: 'What your people get pinged about',
  },
  '/dashboard/settings/chatbot': {
    group: 'AI',
    description: 'Voice agent on the phones',
  },
  '/dashboard/ai/otto': {
    group: 'AI',
    description: 'Dispatcher that routes and warns',
  },
  '/dashboard/settings/integrations': {
    group: 'Developer & data',
    label: 'Apps',
    description: 'Third-party connections',
  },
  '/dashboard/settings/accounting': {
    group: 'Developer & data',
    description: 'Sync to your books',
  },
  '/dashboard/settings/webhooks': {
    group: 'Developer & data',
    description: 'Push events to your endpoints',
  },
  '/dashboard/settings/api-keys': {
    group: 'Developer & data',
    label: 'Partner API',
    description: 'Keys, scopes, rate limits',
  },
  '/dashboard/settings/customfields': {
    group: 'Developer & data',
    description: 'Extra fields on your records',
  },
  '/dashboard/settings/reports': {
    group: 'Developer & data',
    label: 'Reports',
    description: 'Scheduled and shared reports',
  },
};

const PINNED_PATHS = new Set(['/dashboard/settings/subscription', '/dashboard/audit']);

const PRESENTATION_PATH_ORDER = new Map(
  [
    '/dashboard/settings/company',
    '/dashboard/settings/users',
    '/dashboard/settings/permissions',
    '/dashboard/settings/groups',
    '/dashboard/settings/skills',
    '/dashboard/settings/payroll-profiles',
    '/dashboard/settings/office-settings',
    '/dashboard/settings/booking-windows',
    '/dashboard/settings/oncall',
    '/dashboard/settings/online-booking',
    '/dashboard/settings/jobtypes',
    '/dashboard/settings/job-pipelines',
    '/dashboard/settings/contracts',
    '/dashboard/settings/contracts/defaults',
    '/dashboard/settings/service-areas',
    '/dashboard/settings/trades',
    '/dashboard/pricebook',
    '/dashboard/settings/estimates',
    '/dashboard/settings/templates',
    '/dashboard/settings/billing',
    '/dashboard/taxes',
    '/dashboard/settings/terms-and-conditions',
    '/dashboard/settings/scope-of-work',
    '/dashboard/membership-plans',
    '/dashboard/settings/contact-statuses',
    '/dashboard/settings/tags',
    '/dashboard/settings/leads',
    '/dashboard/settings/appointment-forms',
    '/dashboard/settings/checklist',
    '/dashboard/settings/surveys',
    '/dashboard/settings/reviews',
    '/dashboard/settings/channels',
    '/dashboard/settings/phone',
    '/dashboard/settings/call-settings',
    '/dashboard/settings/sms',
    '/dashboard/settings/email-accounts',
    '/dashboard/settings/reminders',
    '/dashboard/settings/notifications',
    '/dashboard/settings/chatbot',
    '/dashboard/ai/otto',
    '/dashboard/settings/integrations',
    '/dashboard/settings/accounting',
    '/dashboard/settings/webhooks',
    '/dashboard/settings/api-keys',
    '/dashboard/settings/customfields',
    '/dashboard/settings/reports',
  ].map((path, index) => [path, index]),
);

export interface PresentedSettingsItem extends NavItem {
  displayLabel: string;
  description: string;
  displayGroup: SettingsGroupName;
}

export interface PresentedSettingsGroup {
  name: SettingsGroupName;
  items: PresentedSettingsItem[];
}

export interface PresentedPinnedSettingsItem extends NavItem {
  displayLabel: string;
  description: string;
  displayGroup: 'Account';
}

type SearchableSettingsItem = PresentedSettingsItem | PresentedPinnedSettingsItem;

const GROUP_ICONS = {
  Company: Building2,
  Operations: CalendarDays,
  'Sales & billing': CreditCard,
  Customers: Users,
  Communications: MessageSquare,
  AI: Sparkles,
  'Developer & data': Braces,
} satisfies Record<SettingsGroupName, typeof Building2>;

function fallbackGroup(group: string | undefined): SettingsGroupName {
  switch (group) {
    case 'Account':
      return 'Company';
    case 'CRM':
      return 'Customers';
    case 'Integrations':
      return 'Developer & data';
    case 'Sales & billing':
    case 'Operations':
    case 'Communications':
    case 'AI':
      return group;
    default:
      return 'Developer & data';
  }
}

export function buildSettingsGroups(items: NavItem[]): PresentedSettingsGroup[] {
  const grouped = new Map<SettingsGroupName, PresentedSettingsItem[]>();

  for (const item of items) {
    if (PINNED_PATHS.has(item.path)) continue;
    const presentation = SETTINGS_PRESENTATION[item.path];
    const displayGroup = presentation?.group ?? fallbackGroup(item.group);
    const presented: PresentedSettingsItem = {
      ...item,
      displayLabel: presentation?.label ?? item.label,
      description: presentation?.description ?? `Manage ${item.label.toLowerCase()} settings`,
      displayGroup,
    };
    grouped.set(displayGroup, [...(grouped.get(displayGroup) ?? []), presented]);
  }

  return GROUP_ORDER.map((name) => ({
    name,
    items: (grouped.get(name) ?? []).sort(
      (a, b) =>
        (PRESENTATION_PATH_ORDER.get(a.path) ?? Number.MAX_SAFE_INTEGER) -
        (PRESENTATION_PATH_ORDER.get(b.path) ?? Number.MAX_SAFE_INTEGER),
    ),
  })).filter((group) => group.items.length > 0);
}

function presentPinnedSettingsItem(item: NavItem): PresentedPinnedSettingsItem {
  const isSubscription = item.path === '/dashboard/settings/subscription';
  return {
    ...item,
    displayLabel: isSubscription ? 'Plan & Billing' : item.label,
    description: isSubscription
      ? 'Manage your plan and billing details'
      : 'Review account activity and changes',
    displayGroup: 'Account',
  };
}

export function filterSettingsSearchItems(
  groups: PresentedSettingsGroup[],
  pinned: NavItem[],
  query: string,
): SearchableSettingsItem[] {
  const normalizedQuery = query.trim().toLowerCase();
  return [
    ...groups.flatMap((group) => group.items),
    ...pinned.map(presentPinnedSettingsItem),
  ].filter((item) =>
    [item.label, item.displayLabel, item.description, item.displayGroup, ...(item.aliases ?? [])]
      .join(' ')
      .toLowerCase()
      .includes(normalizedQuery),
  );
}

export function isSettingsWorkspacePath(pathname: string) {
  if (pathname === '/dashboard/settings' || pathname.startsWith('/dashboard/settings/')) {
    return true;
  }

  return SETTINGS_SECTION.items.some((item) =>
    routeIsActive(pathname, item.path, item.activePaths),
  );
}

interface SettingsOverviewContextValue {
  groups: PresentedSettingsGroup[];
  pinned: NavItem[];
}

const SettingsOverviewContext = createContext<SettingsOverviewContextValue | null>(null);

export function SettingsWorkspace({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const permissions = useAuthPermissions();
  const technicianId = useAuthTechnicianId();
  const fieldMasks = useAuthFieldMasks();
  const costingSettings = useCostingSettings();
  const flags = useFeatureFlags();
  const { fullbleed } = useFullbleed();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [desktopNavCollapsed, setDesktopNavCollapsed] = useState(readSettingsNavCollapsed);

  const visibleItems = useMemo(
    () =>
      SETTINGS_SECTION.items.filter((item) =>
        isNavItemVisible(item, permissions, {
          technicianId,
          costingReportingEnabled: costingSettings.data?.enableReporting === true,
          flags,
          fieldMasks,
        }),
      ),
    [costingSettings.data?.enableReporting, flags, fieldMasks, permissions, technicianId],
  );
  const groups = useMemo(() => buildSettingsGroups(visibleItems), [visibleItems]);
  const pinned = useMemo(
    () => visibleItems.filter((item) => PINNED_PATHS.has(item.path)),
    [visibleItems],
  );
  const overviewContext = useMemo(() => ({ groups, pinned }), [groups, pinned]);
  useEffect(() => setMobileNavOpen(false), [pathname]);
  useEffect(() => {
    const toggleNavigation = (event: Event) => {
      const requestedState = (event as CustomEvent<boolean>).detail;
      setDesktopNavCollapsed((current) => {
        const next = typeof requestedState === 'boolean' ? requestedState : !current;
        persistSettingsNavCollapsed(next);
        return next;
      });
    };
    window.addEventListener(SETTINGS_NAV_TOGGLE_EVENT, toggleNavigation);
    return () => window.removeEventListener(SETTINGS_NAV_TOGGLE_EVENT, toggleNavigation);
  }, []);

  const collapseDesktopNavigation = () => {
    window.dispatchEvent(new CustomEvent(SETTINGS_NAV_TOGGLE_EVENT, { detail: true }));
  };
  const revealNavigationForSearch = useCallback(() => {
    if (window.matchMedia?.('(min-width: 768px)').matches ?? true) {
      window.dispatchEvent(new CustomEvent(SETTINGS_NAV_TOGGLE_EVENT, { detail: false }));
    } else {
      setMobileNavOpen(true);
    }
  }, []);

  return (
    <SettingsOverviewContext.Provider value={overviewContext}>
      <SettingsWorkspaceLayoutProvider>
        <div
          className={cn(
            'relative flex min-w-0 flex-1 overflow-hidden bg-background font-sans text-foreground md:grid md:transition-[grid-template-columns] md:duration-200 md:ease-out motion-reduce:transition-none',
            desktopNavCollapsed
              ? 'md:grid-cols-[0_minmax(0,1fr)]'
              : 'md:grid-cols-[256px_minmax(0,1fr)]',
          )}
        >
          {mobileNavOpen ? (
            <button
              type="button"
              className="absolute inset-0 z-30 bg-black/30 md:hidden"
              aria-label="Close settings navigation"
              onClick={() => setMobileNavOpen(false)}
            />
          ) : null}
          <SettingsNavigation
            groups={groups}
            pinned={pinned}
            pathname={pathname}
            onCollapse={collapseDesktopNavigation}
            desktopNavCollapsed={desktopNavCollapsed}
            mobileNavOpen={mobileNavOpen}
            onRevealForSearch={revealNavigationForSearch}
            className={cn(
              'absolute inset-y-0 left-0 z-40 w-[280px] transition-transform md:static md:z-auto md:w-auto md:translate-x-0 md:transition-opacity md:duration-150',
              desktopNavCollapsed && 'md:pointer-events-none md:invisible md:opacity-0',
              mobileNavOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full',
            )}
          />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background">
            <div className="flex h-12 shrink-0 items-center border-b px-3 md:hidden">
              <button
                type="button"
                className="inline-flex h-8 items-center gap-2 rounded-md px-2 text-sm font-medium text-foreground hover:bg-muted"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open settings navigation"
                aria-expanded={mobileNavOpen}
              >
                <Menu className="size-4" />
                <span>Settings</span>
              </button>
            </div>
            <div
              className={cn(
                'min-h-0 flex-1',
                fullbleed ? 'overflow-y-auto md:overflow-hidden' : 'overflow-y-auto',
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </SettingsWorkspaceLayoutProvider>
    </SettingsOverviewContext.Provider>
  );
}

export function SettingsNavigation({
  groups,
  pinned,
  pathname,
  onCollapse,
  desktopNavCollapsed,
  mobileNavOpen,
  onRevealForSearch,
  className,
}: {
  groups: PresentedSettingsGroup[];
  pinned: NavItem[];
  pathname: string;
  onCollapse: () => void;
  desktopNavCollapsed: boolean;
  mobileNavOpen: boolean;
  onRevealForSearch: () => void;
  className?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const activeGroup = groups.find((group) =>
    group.items.some((item) => routeIsActive(pathname, item.path, item.activePaths)),
  )?.name;
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => ({
    [activeGroup ?? groups[0]?.name ?? 'Company']: true,
  }));

  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((current) => ({ ...current, [activeGroup]: true }));
  }, [activeGroup]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable === true;
      if (event.key === '/' && !isTyping && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        const isDesktop = window.matchMedia?.('(min-width: 768px)').matches ?? true;
        const navigationVisible = isDesktop ? !desktopNavCollapsed : mobileNavOpen;
        if (!navigationVisible) onRevealForSearch();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [desktopNavCollapsed, mobileNavOpen, onRevealForSearch]);

  const normalizedQuery = query.trim().toLowerCase();
  const results = useMemo(
    () => filterSettingsSearchItems(groups, pinned, normalizedQuery),
    [groups, pinned, normalizedQuery],
  );

  return (
    <aside
      className={cn('flex min-h-0 shrink-0 flex-col border-r bg-card text-foreground', className)}
      aria-label="Settings navigation"
    >
      <div className="shrink-0 border-b px-4 pb-3 pt-[18px]">
        <div className="-ml-2 mb-3 flex items-center gap-1">
          <Link
            to="/dashboard/settings"
            className={cn(
              'flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-semibold leading-5 hover:bg-muted',
              isSettingsHome(pathname) && 'font-medium',
            )}
          >
            <Menu className="size-[13px]" />
            All settings
          </Link>
          <button
            type="button"
            className="hidden size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground md:inline-flex"
            onClick={onCollapse}
            aria-label="Collapse settings navigation"
          >
            <PanelLeftClose className="size-4" />
          </button>
        </div>
        <div className="relative flex items-center">
          <Search className="pointer-events-none absolute left-2.5 size-3.5 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setQuery('');
                event.currentTarget.blur();
              }
            }}
            placeholder="Search settings"
            aria-label="Search settings"
            className="w-full rounded-md border bg-background py-2 pl-8 pr-9 text-sm outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20"
          />
          {query ? (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              className="absolute right-2 flex size-[18px] items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="size-2.5" />
            </button>
          ) : (
            <kbd className="pointer-events-none absolute right-2.5 rounded border bg-card px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              /
            </kbd>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-4 pt-2">
        {normalizedQuery ? (
          <div className="py-1">
            <p className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase leading-4 tracking-[0.06em] text-muted-foreground/60">
              {results.length} {results.length === 1 ? 'result' : 'results'}
            </p>
            <div className="flex flex-col gap-px">
              {results.map((item) => (
                <SettingsNavLink key={item.path} item={item} pathname={pathname} showGroup />
              ))}
            </div>
            {results.length === 0 ? (
              <p className="px-2.5 py-3 text-[13px] leading-5 text-muted-foreground">
                Nothing matches that. Try a shorter word, or the thing it does.
              </p>
            ) : null}
          </div>
        ) : (
          groups.map((group) => {
            const open = openGroups[group.name] === true;
            const hasActiveItem = group.items.some((item) =>
              routeIsActive(pathname, item.path, item.activePaths),
            );
            return (
              <div key={group.name} className="mb-0.5">
                <button
                  type="button"
                  className="mt-1.5 flex w-full items-center gap-2 rounded-md border-0 bg-transparent px-2 py-2 text-left text-[11px] font-semibold uppercase leading-4 tracking-[0.06em] text-muted-foreground/60 hover:bg-muted hover:text-foreground"
                  onClick={() => setOpenGroups((current) => ({ ...current, [group.name]: !open }))}
                  aria-expanded={open}
                >
                  <ChevronRight
                    className={cn('size-2.5 shrink-0 stroke-[3]', open && 'rotate-90')}
                  />
                  <span className="flex-1">{group.name}</span>
                  {hasActiveItem && !open ? (
                    <span className="size-[5px] rounded-full bg-foreground" />
                  ) : null}
                  <span className="text-[10px] font-medium text-muted-foreground/60">
                    {group.items.length}
                  </span>
                </button>
                {open ? (
                  <div className="flex flex-col gap-px pb-1">
                    {group.items.map((item) => (
                      <SettingsNavLink key={item.path} item={item} pathname={pathname} />
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {pinned.length > 0 ? (
        <div className="flex shrink-0 flex-col gap-px border-t bg-card px-2.5 py-2">
          {pinned.map((item) => (
            <Link
              key={item.path}
              to={item.path as never}
              className={cn(
                'rounded-md px-2 py-1.5 text-sm font-normal leading-5 hover:bg-muted',
                routeIsActive(pathname, item.path, item.activePaths) && 'bg-muted font-medium',
              )}
            >
              {item.path === '/dashboard/settings/subscription' ? 'Plan & Billing' : item.label}
            </Link>
          ))}
        </div>
      ) : null}
    </aside>
  );
}

function SettingsNavLink({
  item,
  pathname,
  showGroup = false,
}: {
  item: SearchableSettingsItem;
  pathname: string;
  showGroup?: boolean;
}) {
  const active = routeIsActive(pathname, item.path, item.activePaths);
  return (
    <Link
      to={item.path as never}
      className={cn(
        'block rounded-md px-2 py-1.5 text-sm font-normal leading-5 text-foreground/75 transition-colors hover:bg-muted hover:text-foreground',
        !showGroup && 'pl-[26px]',
        active && 'bg-muted font-medium text-foreground',
      )}
    >
      <span className="block">{item.displayLabel}</span>
      {showGroup ? (
        <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
          {item.displayGroup}
        </span>
      ) : null}
    </Link>
  );
}

export function SettingsOverview() {
  const context = useContext(SettingsOverviewContext);
  if (!context) return null;
  return <SettingsOverviewContent groups={context.groups} pinned={context.pinned} />;
}

export function SettingsOverviewContent({ groups, pinned }: SettingsOverviewContextValue) {
  const presentedPinned = pinned.map(presentPinnedSettingsItem);
  const settingsCount =
    groups.reduce((count, group) => count + group.items.length, 0) + presentedPinned.length;
  const groupCount = groups.length + (presentedPinned.length > 0 ? 1 : 0);

  return (
    <div className="mx-auto w-full max-w-[1292px] px-6 pb-16 pt-6 lg:px-8">
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">Settings</h1>
      <p className="mt-1 max-w-[58ch] text-sm text-muted-foreground">
        {settingsCount} settings across {groupCount} groups. Search above, or pick from below.
      </p>

      <div className="mt-8 flex flex-col gap-9">
        {groups.map((group) => {
          const Icon = GROUP_ICONS[group.name];
          return (
            <section key={group.name}>
              <div className="flex items-center gap-3 border-b pb-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Icon className="size-[15px] stroke-[1.75]" />
                </span>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                  {group.name}
                </h2>
                <span className="ml-auto text-xs text-muted-foreground">
                  {group.items.length} settings
                </span>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                {group.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path as never}
                    className="group rounded-xl border bg-card px-4 py-[15px] text-foreground shadow-sm transition hover:bg-muted/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="block text-sm font-medium">{item.displayLabel}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                      {item.description}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
        {presentedPinned.length > 0 ? (
          <section>
            <div className="flex items-center gap-3 border-b pb-3">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <CreditCard className="size-[15px] stroke-[1.75]" />
              </span>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Account
              </h2>
              <span className="ml-auto text-xs text-muted-foreground">
                {presentedPinned.length} settings
              </span>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
              {presentedPinned.map((item) => (
                <Link
                  key={item.path}
                  to={item.path as never}
                  className="group rounded-xl border bg-card px-4 py-[15px] text-foreground shadow-sm transition hover:bg-muted/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="block text-sm font-medium">{item.displayLabel}</span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                    {item.description}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

function routeIsActive(pathname: string, path: string, activePaths: string[] = []) {
  return (
    pathname === path ||
    pathname.startsWith(`${path}/`) ||
    activePaths.some(
      (activePath) => pathname === activePath || pathname.startsWith(`${activePath}/`),
    )
  );
}

function isSettingsHome(pathname: string) {
  return pathname === '/dashboard/settings' || pathname === '/dashboard/settings/';
}

function readSettingsNavCollapsed() {
  try {
    return localStorage.getItem(SETTINGS_NAV_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function persistSettingsNavCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(SETTINGS_NAV_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Persistence is best-effort; the control still works for this session.
  }
}
